import ApolloClient from 'apollo-client'
import createDecorator, { Calculation } from 'final-form-calculate'
import { Procedimento, TipoEstabelecimentoEnum } from 'graphql/types.generated'
import { ProcedimentosAutomaticoAtendimentoUpa, ProcedimentosAutomaticosEscutaInicial } from 'types/enums'
import { emptyArray } from 'util/array'
import { MetaArray, MetaPath } from 'util/metaPath'

import { createAgendarConsultasCalculations } from '../agenda/calculator-agendarConsulta'
import { AntropometriaAccordionModel } from '../components/MedicoesPanel/AntropometriaAccordion'
import { createSinaisVitaisEscutaCalculations } from '../components/MedicoesPanel/calculator-sinaisVitais'
import { SinaisVitaisAccordionModel } from '../components/MedicoesPanel/SinaisVitaisAccordion'
import { createNovoAtendimentoCalculations } from '../detail/soap/finalizacao/calculator/calculatorNovoAtendimento'
import { ProcedimentoSigtapFieldModel } from '../detail/soap/finalizacao/components/ProcedimentoSigtapField'
import {
  addOrRemoveAfericaoPressaoArterial,
  addOrRemoveAfericaoTemperatura,
  addOrRemoveGlicemiaCapilar,
  addProcedimentoAutomatico,
  removeProcedimentoAutomatico,
  shouldAddProcedimentoAutomatico,
} from '../utils/util-procedimentoAutomatico'
import { EscutaInicialState, name } from './model-escutaInicial'

export const createEscutaInicialCalculator = (
  procedimentosAutomaticos: Array<Procedimento>,
  tipoEstabelecimento: TipoEstabelecimentoEnum,
  apollo: ApolloClient<object>
) =>
  createDecorator(
    ...createSinaisVitaisEscutaCalculations(name.sinaisVitais),
    ...createProcedimentosAutomaticosCalculations(
      name.sinaisVitais,
      name.antropometria,
      procedimentosAutomaticos,
      tipoEstabelecimento
    ),
    ...createNovoAtendimentoCalculations(name.desfecho.atendimento, tipoEstabelecimento, apollo),
    ...createAgendarConsultasCalculations(name.agendamentoConsulta.avulsa)
  )

const createProcedimentosAutomaticosCalculations = (
  sinaisVitais: MetaPath<SinaisVitaisAccordionModel>,
  antropometria: MetaPath<AntropometriaAccordionModel>,
  procedimentosAutomaticos: Array<Procedimento>,
  tipoEstabelecimento: TipoEstabelecimentoEnum
): Calculation[] => [
  {
    field: [antropometria.peso.absolutePath(), antropometria.altura.absolutePath()],
    updates: {
      [name.procedimentos.procedimentosSigtap.absolutePath()]: (_, allValues: EscutaInicialState) => {
        let listaProcedimentos = allValues.procedimentos?.procedimentosSigtap ?? emptyArray
        const codigosProcedimentosAutomaticosExcluidos =
          allValues.procedimentos?.codigosProcedimentosAutomaticosExcluidos ?? emptyArray

        const codigoProcedimentoSeraAdicionado = procedToAddEscutaInicial(allValues)

        if (
          codigoProcedimentoSeraAdicionado &&
          shouldAddProcedimentoAutomatico(codigoProcedimentoSeraAdicionado, codigosProcedimentosAutomaticosExcluidos)
        ) {
          listaProcedimentos = addProcedimentoAutomatico(
            listaProcedimentos,
            procedimentosAutomaticos,
            codigoProcedimentoSeraAdicionado
          )
        }

        PROCEDIMENTOS_TO_REMOVE.filter((proced) => proced !== codigoProcedimentoSeraAdicionado).forEach(
          (procedimento: ProcedimentosAutomaticosEscutaInicial) => {
            listaProcedimentos = removeProcedimentoAutomatico(listaProcedimentos, procedimento)
          }
        )

        return listaProcedimentos
      },
    },
  },
  {
    field: [
      sinaisVitais.pressaoArterialSistolica.absolutePath(),
      sinaisVitais.pressaoArterialDiastolica.absolutePath(),
    ],
    updates: {
      [name.procedimentos.procedimentosSigtap.absolutePath()]: (_, allValues: EscutaInicialState) => {
        const listaProcedimentos = allValues.procedimentos?.procedimentosSigtap ?? emptyArray
        const codigosProcedimentosAutomaticosExcluidos =
          allValues.procedimentos?.codigosProcedimentosAutomaticosExcluidos ?? emptyArray

        return addOrRemoveAfericaoPressaoArterial(
          allValues.sinaisVitais?.pressaoArterialSistolica,
          allValues.sinaisVitais?.pressaoArterialDiastolica,
          listaProcedimentos,
          procedimentosAutomaticos,
          codigosProcedimentosAutomaticosExcluidos,
          ProcedimentosAutomaticosEscutaInicial.AFERICAO_PRESSAO_ARTERIAL
        )
      },
    },
  },
  {
    field: sinaisVitais.glicemia.absolutePath(),
    updates: {
      [name.procedimentos.procedimentosSigtap.absolutePath()]: (_, allValues: EscutaInicialState) => {
        const listaProcedimentos = allValues.procedimentos?.procedimentosSigtap ?? emptyArray
        const codigosProcedimentosAutomaticosExcluidos =
          allValues.procedimentos?.codigosProcedimentosAutomaticosExcluidos ?? emptyArray

        return addOrRemoveGlicemiaCapilar(
          allValues.sinaisVitais?.glicemia,
          true,
          listaProcedimentos,
          procedimentosAutomaticos,
          codigosProcedimentosAutomaticosExcluidos,
          ProcedimentosAutomaticosEscutaInicial.GLICEMIA_CAPILAR
        )
      },
    },
  },
  {
    field: sinaisVitais.temperatura.absolutePath(),
    updates: {
      [name.procedimentos.procedimentosSigtap.absolutePath()]: (_, allValues: EscutaInicialState) => {
        const listaProcedimentos = allValues.procedimentos?.procedimentosSigtap ?? emptyArray
        const codigosProcedimentosAutomaticosExcluidos =
          allValues.procedimentos?.codigosProcedimentosAutomaticosExcluidos ?? emptyArray

        return addOrRemoveAfericaoTemperatura(
          allValues.sinaisVitais?.temperatura,
          listaProcedimentos,
          procedimentosAutomaticos,
          codigosProcedimentosAutomaticosExcluidos,
          ProcedimentosAutomaticosEscutaInicial.AFERICAO_DE_TEMPERATURA
        )
      },
    },
  },
  {
    field: name.classificacaoRisco.absolutePath(),
    updates: {
      [name.procedimentosAdministrativos.absolutePath()]: (
        _: MetaArray<ProcedimentoSigtapFieldModel>,
        allValues: EscutaInicialState
      ) => {
        const classificacaoRiscoHasValue = allValues.classificacaoRisco
        const hasClassificacaoAndIsUpa =
          classificacaoRiscoHasValue && tipoEstabelecimento === TipoEstabelecimentoEnum.UPA

        const listaProcedimentos = allValues.procedimentosAdministrativos ?? emptyArray

        if (hasClassificacaoAndIsUpa) {
          return addProcedimentoAutomatico(
            listaProcedimentos,
            procedimentosAutomaticos,
            ProcedimentosAutomaticoAtendimentoUpa.ACOLHIMENTO_COM_CLASSIFICACAO_RISCO
          )
        }

        return removeProcedimentoAutomatico(
          listaProcedimentos,
          ProcedimentosAutomaticoAtendimentoUpa.ACOLHIMENTO_COM_CLASSIFICACAO_RISCO
        )
      },
    },
  },
]

function procedToAddEscutaInicial(allValues: EscutaInicialState): ProcedimentosAutomaticosEscutaInicial {
  const peso = allValues.antropometria?.peso
  const altura = allValues.antropometria?.altura

  if (peso && altura) {
    return ProcedimentosAutomaticosEscutaInicial.AVALIACAO_ANTROPOMETRICA
  } else if (peso) {
    return ProcedimentosAutomaticosEscutaInicial.MEDICAO_DE_PESO
  } else if (altura) {
    return ProcedimentosAutomaticosEscutaInicial.MEDICAO_DE_ALTURA
  } else {
    return null
  }
}

const PROCEDIMENTOS_TO_REMOVE = [
  ProcedimentosAutomaticosEscutaInicial.MEDICAO_DE_PESO,
  ProcedimentosAutomaticosEscutaInicial.MEDICAO_DE_ALTURA,
  ProcedimentosAutomaticosEscutaInicial.AVALIACAO_ANTROPOMETRICA,
]
